import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';

import { fetch as axios } from '../../axios_config';

import { handleRejectedResponse } from './utils';
import { isEmpty } from 'lodash';

export const GetWarehouseShipments = createAsyncThunk(
  'post-shipments/warehouse-shipments',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/warehouse-shipments`,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Warehouse Shipments', rejectWithValue);
    }
  }
);

export const CreateMultiSkuBox = createAsyncThunk(
  'post-shipments/create-multi-sku-box',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/create-multi-sku-box`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Create Multi-SKU Box', rejectWithValue);
    }
  }
);

export const CreateSingleSkuBox = createAsyncThunk(
  'post-shipments/create-single-sku-box',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/create-single-sku-box`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Create Single SKU Box', rejectWithValue);
    }
  }
);

export const CreatePallets = createAsyncThunk(
  'post-shipments/pallets',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/pallets`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Create Pallets', rejectWithValue);
    }
  }
);

export const GenerateTransportationOptions = createAsyncThunk(
  'post-shipments/generate-transportation-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/generate-transportation-options`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Generate Transportation Options', rejectWithValue);
    }
  }
);

export const GenerateDeliveryWindowOptions = createAsyncThunk(
  'post-shipments/generate-delivery-window-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/generate-delivery-window-options`,
        null,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Generate Delivery Window Options', rejectWithValue);
    }
  }
);

export const ConfirmDeliveryWindowOption = createAsyncThunk(
  'post-shipments/confirm-delivery-window-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/confirm-delivery-window-options`,
        null,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Confirm Delivery Window Option', rejectWithValue);
    }
  }
);

export const ConfirmTransportationOptions = createAsyncThunk(
  'post-shipments/confirm-transportation-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/confirm-transportation-options`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Confirm Transportation Options', rejectWithValue);
    }
  }
);

export const ClearBoxItems = createAsyncThunk(
  'post-shipments/clear-box-items',
  async (boxId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/clear-box-items?boxId=${boxId}`,
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Clear Box Items', rejectWithValue);
    }
  }
);

export const DeleteBox = createAsyncThunk(
  'post-shipments/box',
  async (boxId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/box?boxId=${boxId}`,
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Delete Box', rejectWithValue);
    }
  }
);

export const DeleteBoxItem = createAsyncThunk(
  'post-shipments/box-item',
  async (boxItemBody, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/box-item`,
        {
          data: boxItemBody
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Delete Box Item', rejectWithValue);
    }
  }
);

export const UpdateBox = createAsyncThunk(
  'post-shipments/box',
  async (data, { rejectWithValue }) => {
    try {
      const { boxId } = data;
      delete data.boxId;

      const response = await axios.put(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/box?boxId=${boxId}`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Box', rejectWithValue);
    }
  }
);

export const GetBoxItems = createAsyncThunk(
  'post-shipments/box-items',
  async (boxId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/box-items?boxId=${boxId}`,
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Box Items', rejectWithValue);
    }
  }
);

export const ListTransportationOptions = createAsyncThunk(
  'post-shipments/list-transportation-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/list-transportation-options`,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'List Transportation Options', rejectWithValue);
    }
  }
);

export const RevertInboundPlanStatus = createAsyncThunk(
  'post-shipments/update-inbound-plan',
  async (data, {rejectWithValue}) => {
    try {
      const response = await axios.put(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/update-inbound-plan`,
        data
      );
      return response?.data;
      
    } catch (error) {
      return handleRejectedResponse(error, 'Revert Inbound Plan Status', rejectWithValue);
    }
  }
)

export const ListDeliveryWindowOptions = createAsyncThunk(
  'post-shipments/list-delivery-window-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/list-delivery-window-options`,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'List Delivery Window Options', rejectWithValue);
    }
  }
);

export const SetPackingInformation = createAsyncThunk(
  'post-shipments/set-packing-information',
  async (workOrderId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/set-packing-information?workOrderId=${workOrderId}`,
      );
      return response?.data;
    } catch (err) {
      const boxErrors = err?.response?.data?.boxErrors || [];
      handleRejectedResponse(err, 'Set Packing Information', rejectWithValue);
      return rejectWithValue({ message: err?.response?.data?.message, boxErrors });
    }
  }
);

export const UpdateShipmentTrackingDetails = createAsyncThunk(
  'post-shipments/update-shipment-tracking-details',
  async (body, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/update-shipment-tracking-details`,
        body
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Shipment Tracking Details', rejectWithValue);
    }
  }
);

export const DownloadPickList = createAsyncThunk(
  'post-shipments/pick-list',
  async (workOrderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/download-pick-list/${workOrderId}`,
      );
      return response?.data;
    } catch (err) {
      // 404 = no logs found (normal for pack_now where items aren't picked from storage)
      if (err?.response?.status === 404) {
        return rejectWithValue({ statusCode: 404, message: 'No logs found', silent: true });
      }
      return handleRejectedResponse(err, 'Download Pick List', rejectWithValue);
    }
  }
);

export const DownloadBillOfLading = createAsyncThunk(
  'post-shipments/downloadBillOfLading',
  async (shipmentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/bill-of-lading`,
        {
          params: { shipmentId }
        }
      );
      const { DownloadURL = '' } = response?.data || {};
      if (!isEmpty(DownloadURL)) window.open(DownloadURL);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Downloading Bill Of Lading', rejectWithValue);
    }
  }
);

export const GetLabels = createAsyncThunk(
  'post-shipments/get-labels',
  async (body, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/labels`,
        body
      );
      const { DownloadURL = '' } = response?.data || {};
      if (!isEmpty(DownloadURL)) window.open(DownloadURL);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Downloading Labels', rejectWithValue);
    }
  }
);

export const SaveTransportationDetails = createAsyncThunk(
  'post-shipments/save-transportation-details',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/save-transportation-details`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Save transportation Details', rejectWithValue);
    }
  }
);

export const PreparePacking = createAsyncThunk(
  'post-shipments/prepare-packing',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/prepare-packing`,
        {},
        { params: data }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Prepare Packing', rejectWithValue);
    }
  }
);

export const ConfirmPackingOption = createAsyncThunk(
  'post-shipments/confirm-packing-option',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/confirm-packing-option`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Confirm Packing Option', rejectWithValue);
    }
  }
);

export const GetPackingGroupItems = createAsyncThunk(
  'post-shipments/list-packing-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/list-packing-options`,
        { params: data }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Packing Group Items', rejectWithValue);
    }
  }
);

export const GetPackingItems = createAsyncThunk(
  'post-shipments/get-packing-items',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/packing-items`,
        { params: data }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Packing Items', rejectWithValue);
    }
  }
);
  
export const ListPlacementOptions = createAsyncThunk(
  'post-shipments/list-placement-options',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/list-placement-options`,
        { params: data }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'List Placement Options', rejectWithValue);
    }
  }
);

export const ConfirmPlacementOption = createAsyncThunk(
  'post-shipments/confirm-placement-option',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/confirm-placement-option`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Confirm Placement Option', rejectWithValue);
    }
  }
);

export const GetShipmentBoxes = createAsyncThunk(
  'post-shipments/get-shipment-boxes',
  async (shipmentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/shipment-boxes`,
        { params: { shipmentId } }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Shipment Boxes', rejectWithValue);
    }
  }
);

export const GetPackScanInfo = createAsyncThunk(
  'post-shipments/get-pack-scan-info',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/pack-scan-info`,
        { params: data }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Pack Scan Info', rejectWithValue);
    }
  }
);

export const DeletePackScanInfo = createAsyncThunk(
  'post-shipments/delete-pack-scan-info',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.OPULENT_NODE_API_URL}/post-shipments/pack-scan-info`,
        { params: data }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Delete Pack Scan Info', rejectWithValue);
    }
  }
);

const postShipment = createSlice({
  name: 'postShipment',
  initialState: {
    fetchingWarehouseShipments: false,
    shipmentDetails: {},

    creatingMultiSkuBox: false,
    creatingSingleSkuBox: false,
    clearingBoxItems: false,
    deletingBox: false,
    deletingBoxItem: false,
    updatingBox: false,

    fetchingBoxItems: false,
    boxItems: [],
    settingPackInformation: false,
    creatingPallets: false,
    generatingTransportationOptions: false,
    generatingDeliveryWindowOptions: false,
    confirmingDeliveryWindowOption: false,
    confirmingTransportationOption: false,
    bolLoading: false,
    getLabelsLoading: false,
    saveTransportationDetailsLoading: false,

    listingTransportationOptions: false,
    revertingInboundStatus: false,
    transportationOptions: [],

    listingDeliveryWindowOptions: false,
    deliveryWindowOptions: {},
    updatingShipmentTrackingDetails: false,
    listingPlacementOptions: false,
    confirmingPlacementOption: false,
    placementOptions: [],
    allBoxes: [],

    packingMethod: 'pack_later',
    shipmentType: 'SP',
    isPartnered: true,
    packingGroups: [],
    packingOptionId: '',
    fetchingPackingGroups: false,
    packingItems: [],
    packingBoxes: [],
    boxRequirements: null,
    boxWarningMap: {},
    fetchingPackingItems: false,
    skuGroupMap: {},
    packingOptions: [],
    confirmingPackingOption: false,
  },
  reducers: {
    setShipmentStates(state, { payload: { field, value } }) {
      state[field] = value;
    },
    setPackingPreferences(state, { payload: { packingMethod, shipmentType, isPartnered, packingGroups, packingOptionId } }) {
      state.packingMethod = packingMethod;
      state.shipmentType = shipmentType;
      state.isPartnered = isPartnered;
      if (packingGroups !== undefined) state.packingGroups = packingGroups;
      if (packingOptionId !== undefined) state.packingOptionId = packingOptionId;
    },
    resetPostShipmentState(state) {
      state.shipmentDetails = {};
      state.allBoxes = [];
      state.packingGroups = [];
      state.packingOptionId = '';
      state.packingItems = [];
      state.skuGroupMap = {};
      state.packingBoxes = [];
      state.boxWarningMap = {};
      state.transportationOptions = [];
      state.deliveryWindowOptions = {};
      state.placementOptions = [];
      state.packingMethod = 'pack_later';
      state.shipmentType = 'SP';
      state.fetchingWarehouseShipments = false;
    },
    clearTransportationOptions(state) {
      state.transportationOptions = {
        nonPartneredOptions: [],
        partneredOptions: []
      };
    }
  },
  extraReducers: {
    [GetWarehouseShipments.pending]: (state) => {
      state.fetchingWarehouseShipments = true;
    },
    [GetWarehouseShipments.fulfilled]: (state, action) => {
      const { shipmentDetails } = action?.payload || {};

      state.shipmentDetails = shipmentDetails;
      state.allBoxes = shipmentDetails?.shipments?.map(shipment => shipment.boxes_info)?.flat() || [];

      if (shipmentDetails?.shipmentType) {
        // Use the authoritative shipment_type stored on the inbound plan
        state.shipmentType = shipmentDetails.shipmentType;
      } else if (shipmentDetails?.shipments) {
        // Fallback: derive from pallet presence (legacy plans without shipment_type)
        const hasPallets = shipmentDetails.shipments.some(s => {
          let p = s.pallets;
          if (typeof p === 'string') {
            try { p = JSON.parse(p); } catch (e) { p = []; }
          }
          return Array.isArray(p) && p.length > 0;
        });
        state.shipmentType = hasPallets ? 'LTL' : 'SP';
      }

      if (shipmentDetails?.packingMethod) {
        state.packingMethod = shipmentDetails.packingMethod;
      }
      if (shipmentDetails?.packingGroupId) {
        state.packingGroups = shipmentDetails.packingGroupId.split(',').map(id => ({ packingGroupId: id }));
        state.packingOptionId = shipmentDetails.packingOptionId || '';
      }

      state.fetchingWarehouseShipments = false;
    },
    [GetWarehouseShipments.rejected]: (state) => {
      state.fetchingWarehouseShipments = false;
    },

    [CreateMultiSkuBox.pending]: (state) => {
      state.creatingMultiSkuBox = true;
    },
    [CreateMultiSkuBox.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.creatingMultiSkuBox = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [CreateMultiSkuBox.rejected]: (state) => {
      state.creatingMultiSkuBox = false;
    },

    [ClearBoxItems.pending]: (state) => {
      state.clearingBoxItems = true;
    },
    [ClearBoxItems.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.clearingBoxItems = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [ClearBoxItems.rejected]: (state) => {
      state.clearingBoxItems = false;
    },

    [CreatePallets.pending]: (state) => {
      state.creatingPallets = true;
    },
    [CreatePallets.fulfilled]: (state, action) => {
      state.creatingPallets = false;
    },
    [CreatePallets.rejected]: (state) => {
      state.creatingPallets = false;
    },

    [CreateSingleSkuBox.pending]: (state) => {
      state.creatingSingleSkuBox = true;
    },
    [CreateSingleSkuBox.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.creatingSingleSkuBox = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [CreateSingleSkuBox.rejected]: (state) => {
      state.creatingSingleSkuBox = false;
    },

    [DeleteBox.pending]: (state) => {
      state.deletingBox = true;
    },
    [DeleteBox.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.deletingBox = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [DeleteBox.rejected]: (state) => {
      state.deletingBox = false;
    },

    [DeleteBoxItem.pending]: (state) => {
      state.deletingBoxItem = true;
    },
    [DeleteBoxItem.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.deletingBoxItem = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [DeleteBoxItem.rejected]: (state) => {
      state.deletingBoxItem = false;
    },

    [UpdateBox.pending]: (state) => {
      state.updatingBox = true;
    },
    [UpdateBox.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.updatingBox = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [UpdateBox.rejected]: (state) => {
      state.updatingBox = false;
    },

    [ListDeliveryWindowOptions.pending]: (state) => {
      state.listingDeliveryWindowOptions = true;
    },
    [ListDeliveryWindowOptions.fulfilled]: (state, action) => {
      const { deliveryWindowOptions } = action?.payload || {};
      const { shipmentId } = action.meta.arg || {};

      state.listingDeliveryWindowOptions = false;
      state.deliveryWindowOptions = deliveryWindowOptions;
    },
    [ListDeliveryWindowOptions.rejected]: (state) => {
      state.listingDeliveryWindowOptions = false;
    },

    [ListTransportationOptions.pending]: (state) => {
      state.listingTransportationOptions = true;
    },
    [ListTransportationOptions.fulfilled]: (state, action) => {
      const { nonPartneredOptions, partneredOptions, isGenerated } = action?.payload || {};

      state.listingTransportationOptions = false;
      state.transportationOptions = {
        nonPartneredOptions,
        partneredOptions
      };
      let message = 'Transportation Options Re-Generated Successfully, Please Select The New Transportation Options';

      isGenerated && notification['success']({
        message,
        top: 65,
      });
    },
    [ListTransportationOptions.rejected]: (state) => {
      state.listingTransportationOptions = false;
    },

    [RevertInboundPlanStatus.pending]: (state) => {
      state.revertingInboundStatus = true
    },
    [RevertInboundPlanStatus.fulfilled]: (state) => {
      state.revertingInboundStatus = false
    },
    [RevertInboundPlanStatus.rejected]: (state) => {
      state.revertingInboundStatus = false
    },

    [GetBoxItems.pending]: (state) => {
      state.fetchingBoxItems = true;
    },
    [GetBoxItems.fulfilled]: (state, action) => {
      const { boxItems } = action?.payload || {};

      state.fetchingBoxItems = false;
      state.boxItems = boxItems;
    },
    [GetBoxItems.rejected]: (state) => {
      state.fetchingBoxItems = false;
    },

    [SetPackingInformation.pending]: (state) => {
      state.settingPackInformation = true;
    },
    [SetPackingInformation.fulfilled]: (state, action) => {
      const { message, success, boxWarnings } = action?.payload || {};

      state.settingPackInformation = false;
      if (boxWarnings?.length) {
        const map = {};
        for (const w of boxWarnings) {
          if (w.internalBoxId != null) {
            const key = String(w.internalBoxId);
            if (!map[key]) map[key] = [];
            map[key].push({ message: w.message, severity: w.severity });
          }
        }
        state.boxWarningMap = map;
      }
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [SetPackingInformation.rejected]: (state, action) => {
      state.settingPackInformation = false;
      const boxErrors = action?.payload?.boxErrors || [];
      if (boxErrors.length) {
        const map = {};
        for (const e of boxErrors) {
          if (e.internalBoxId != null) {
            const key = String(e.internalBoxId);
            if (!map[key]) map[key] = [];
            map[key].push({ message: e.message, severity: e.severity });
          }
        }
        state.boxWarningMap = map;
      }
    },

    [ConfirmTransportationOptions.pending]: (state) => {
      state.confirmingTransportationOption = true;
    },
    [ConfirmTransportationOptions.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.confirmingTransportationOption = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [ConfirmTransportationOptions.rejected]: (state) => {
      state.confirmingTransportationOption = false;
    },

    [ConfirmDeliveryWindowOption.pending]: (state) => {
      state.confirmingDeliveryWindowOption = true;
    },
    [ConfirmDeliveryWindowOption.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.confirmingDeliveryWindowOption = false;
    },
    [ConfirmDeliveryWindowOption.rejected]: (state) => {
      state.confirmingDeliveryWindowOption = false;
    },

    [UpdateShipmentTrackingDetails.pending]: (state) => {
      state.updatingShipmentTrackingDetails = true;
    },
    [UpdateShipmentTrackingDetails.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.updatingShipmentTrackingDetails = false;
    },
    [UpdateShipmentTrackingDetails.rejected]: (state) => {
      state.updatingShipmentTrackingDetails = false;
    },

    [GenerateTransportationOptions.pending]: (state) => {
      state.generatingTransportationOptions = true;
    },
    [GenerateTransportationOptions.fulfilled]: (state, action) => {
      const { message, success, errors } = action?.payload || {};

      state.generatingTransportationOptions = false;
      const generalErrors = errors?.generalErrors || [];
      notification[success ? 'success' : 'error']({
        message,
        description: !success && generalErrors.length > 0 ? generalErrors.join(' | ') : undefined,
        top: 65,
        duration: success ? 4 : 10,
      });
    },
    [GenerateTransportationOptions.rejected]: (state) => {
      state.generatingTransportationOptions = false;
    },

    [GenerateDeliveryWindowOptions.pending]: (state) => {
      state.generatingDeliveryWindowOptions = true;
    },
    [GenerateDeliveryWindowOptions.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.generatingDeliveryWindowOptions = false;
    },
    [GenerateDeliveryWindowOptions.rejected]: (state) => {
      state.generatingDeliveryWindowOptions = false;
    },
    [DownloadBillOfLading.pending]: (state) => ({
      ...state,
      bolLoading: true
    }),
    [DownloadBillOfLading.fulfilled]: (state, { payload }) => {
      const {
        DownloadURL,
        message = ''
      } = payload || {};
      if (isEmpty(DownloadURL)) {
        notification['error']({
          message,
          top: 65,
        });
      }
      else {
        notification['success']({
          message,
          top: 65,
        });
      }
      return {
        ...state,
        bolLoading: false,
        success: true
      };
    },
    [DownloadBillOfLading.rejected]: (state, { payload }) => {
      return {
        ...state,
        bolLoading: false
      }
    },
    [GetLabels.pending]: (state) => ({
      ...state,
      getLabelsLoading: true
    }),
    [GetLabels.fulfilled]: (state, { payload }) => {
      const {
        DownloadURL,
        message = ''
      } = payload || {};
      if (isEmpty(DownloadURL)) {
        notification['error']({
          message,
          top: 65,
        });
      }
      else {
        notification['success']({
          message,
          top: 65,
        });
      }
      return {
        ...state,
        getLabelsLoading: false,
        success: true
      };
    },
    [GetLabels.rejected]: (state, { payload }) => {
      return {
        ...state,
        getLabelsLoading: false
      }
    },
    [SaveTransportationDetails.pending]: (state) => ({
      ...state,
      saveTransportationDetailsLoading: true
    }),
    [SaveTransportationDetails.fulfilled]: (state, { payload }) => {
      if (payload?.success) {
        // notification['success']({
        //   message: 'Transportation Details Saved Successfully',
        //   top: 65,
        // });
      }
      return {
        ...state,
        saveTransportationDetailsLoading: false,
        success: true
      };
    },
    [SaveTransportationDetails.rejected]: (state, { payload }) => {
      return {
        ...state,
        saveTransportationDetailsLoading: false
      }
    },

    [PreparePacking.pending]: (state) => {
      state.fetchingPackingGroups = true;
    },
    [PreparePacking.fulfilled]: (state, action) => {
      const { packingOptions, currentOptionId, currentGroupId, packingGroups } = action?.payload || {};
      state.fetchingPackingGroups = false;
      state.packingOptions = packingOptions || [];
      state.packingOptionId = currentOptionId || '';
      if (packingGroups?.length) {
        state.packingGroups = packingGroups;
      } else if (currentGroupId) {
        state.packingGroups = currentGroupId.split(',').map(id => ({ packingGroupId: id }));
      }
    },
    [PreparePacking.rejected]: (state) => {
      state.fetchingPackingGroups = false;
    },

    [ConfirmPackingOption.pending]: (state) => {
      state.confirmingPackingOption = true;
    },
    [ConfirmPackingOption.fulfilled]: (state, action) => {
      const { packingOptionId, packingGroupId, packingGroups, success, message } = action?.payload || {};
      state.confirmingPackingOption = false;
      if (success) {
        state.packingOptionId = packingOptionId;
        state.packingGroups = (packingGroups && packingGroups.length > 0) 
          ? packingGroups 
          : (packingGroupId ? packingGroupId.split(',').map(id => ({ packingGroupId: id })) : []);
      }
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [ConfirmPackingOption.rejected]: (state) => {
      state.confirmingPackingOption = false;
    },

    [GetPackingGroupItems.pending]: (state) => {
      state.fetchingPackingGroups = true;
    },
    [GetPackingGroupItems.fulfilled]: (state, action) => {
      const { packingGroups, packingOptionId, skuGroupMap } = action?.payload || {};
      state.fetchingPackingGroups = false;
      if (packingGroups?.length) {
        state.packingGroups = packingGroups;
        state.packingOptionId = packingOptionId || '';
      }
      if (skuGroupMap && Object.keys(skuGroupMap).length) {
        state.skuGroupMap = skuGroupMap;
      }
    },
    [GetPackingGroupItems.rejected]: (state) => {
      state.fetchingPackingGroups = false;
    },

    [GetPackingItems.pending]: (state) => {
      state.fetchingPackingItems = true;
    },
    [GetPackingItems.fulfilled]: (state, action) => {
      state.fetchingPackingItems = false;
      state.packingItems = action?.payload?.packingItems || [];
      state.packingBoxes = action?.payload?.packingBoxes || [];
      if (action?.payload?.boxRequirements) {
        state.boxRequirements = action.payload.boxRequirements;
      }
      if (action?.payload?.packingGroups?.length) {
        state.packingGroups = action.payload.packingGroups;
      }
      if (action?.payload?.skuGroupMap) {
        state.skuGroupMap = action.payload.skuGroupMap;
      }
    },
    [GetPackingItems.rejected]: (state) => {
      state.fetchingPackingItems = false;
    },

    [ListPlacementOptions.pending]: (state) => {
      state.listingPlacementOptions = true;
    },
    [ListPlacementOptions.fulfilled]: (state, action) => {
      state.listingPlacementOptions = false;
      state.placementOptions = action?.payload?.placementOptions || [];
    },
    [ListPlacementOptions.rejected]: (state) => {
      state.listingPlacementOptions = false;
    },

    [ConfirmPlacementOption.pending]: (state) => {
      state.confirmingPlacementOption = true;
    },
    [ConfirmPlacementOption.fulfilled]: (state, action) => {
      state.confirmingPlacementOption = false;
      const { message, success } = action?.payload || {};
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [ConfirmPlacementOption.rejected]: (state) => {
      state.confirmingPlacementOption = false;
    },
  },
});

const { reducer, actions } = postShipment;

export const { setShipmentStates, setPackingPreferences, clearTransportationOptions, resetPostShipmentState } = actions;

export default reducer;
